import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Shield } from 'lucide-react';
import { toast } from 'react-hot-toast';
import connectorService from '../../services/connector.service';
import type { ConnectorAcl, CreateAclData, UpdateAclData } from '../../services/connector.service';
import DataTable, { type Column } from '../common/DataTable';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';

interface ACLTabProps {
  connectorId: string;
}

const ACLTab = ({ connectorId }: ACLTabProps) => {
  const [rules, setRules] = useState<ConnectorAcl[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRule, setEditRule] = useState<ConnectorAcl | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; rule?: ConnectorAcl }>({ show: false });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState<CreateAclData>({
    ruleName: '',
    priority: 100,
    direction: 'inbound',
    sourceCidr: '',
    destCidr: '',
    protocol: 'all',
    portRange: '',
    action: 'allow',
    enabled: true,
    description: '',
  });

  const fetchRules = async () => {
    try {
      setLoading(true);
      const data = await connectorService.listAcl(connectorId);
      setRules(data);
    } catch {
      toast.error('Failed to load ACL rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRules(); }, [connectorId]);

  const openAdd = () => {
    setEditRule(null);
    setFormData({ ruleName: '', priority: 100, direction: 'inbound', sourceCidr: '', destCidr: '', protocol: 'all', portRange: '', action: 'allow', enabled: true, description: '' });
    setModalOpen(true);
  };

  const openEdit = (rule: ConnectorAcl) => {
    setEditRule(rule);
    setFormData({
      ruleName: rule.rule_name,
      priority: rule.priority,
      direction: rule.direction,
      sourceCidr: rule.source_cidr || '',
      destCidr: rule.dest_cidr || '',
      protocol: rule.protocol || 'all',
      portRange: rule.port_range || '',
      action: rule.action,
      enabled: rule.enabled === 1,
      description: rule.description || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editRule) {
        await connectorService.updateAcl(connectorId, editRule.id, formData as UpdateAclData);
        toast.success('ACL rule updated');
      } else {
        await connectorService.addAcl(connectorId, formData);
        toast.success('ACL rule added');
      }
      setModalOpen(false);
      fetchRules();
    } catch {
      toast.error(editRule ? 'Failed to update rule' : 'Failed to add rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.rule) return;
    try {
      setDeleting(true);
      await connectorService.deleteAcl(connectorId, deleteConfirm.rule.id);
      toast.success('ACL rule deleted');
      setDeleteConfirm({ show: false });
      fetchRules();
    } catch {
      toast.error('Failed to delete rule');
    } finally {
      setDeleting(false);
    }
  };

  const columns: Column<ConnectorAcl>[] = [
    {
      key: 'priority',
      label: 'Priority',
      render: (rule) => <span className="font-mono text-sm">{rule.priority}</span>,
    },
    {
      key: 'rule_name',
      label: 'Name',
      render: (rule) => rule.rule_name,
    },
    {
      key: 'direction',
      label: 'Direction',
      render: (rule) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${rule.direction === 'inbound' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
          {rule.direction}
        </span>
      ),
    },
    {
      key: 'source_cidr',
      label: 'Source',
      render: (rule) => rule.source_cidr ? <code className="text-xs bg-gray-100 px-1 rounded">{rule.source_cidr}</code> : <span className="text-gray-400">any</span>,
    },
    {
      key: 'dest_cidr',
      label: 'Dest',
      render: (rule) => rule.dest_cidr ? <code className="text-xs bg-gray-100 px-1 rounded">{rule.dest_cidr}</code> : <span className="text-gray-400">any</span>,
    },
    {
      key: 'protocol',
      label: 'Proto',
      render: (rule) => <span className="text-sm uppercase">{rule.protocol || 'all'}</span>,
    },
    {
      key: 'port_range',
      label: 'Ports',
      render: (rule) => rule.port_range || <span className="text-gray-400">all</span>,
    },
    {
      key: 'action',
      label: 'Action',
      render: (rule) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${rule.action === 'allow' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {rule.action}
        </span>
      ),
    },
    {
      key: 'enabled',
      label: 'Status',
      render: (rule) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${rule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
          {rule.enabled ? 'On' : 'Off'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (rule) => (
        <div className="flex items-center space-x-2">
          <button onClick={() => openEdit(rule)} className="text-gray-500 hover:text-primary-600"><Pencil className="w-4 h-4" /></button>
          <button onClick={() => setDeleteConfirm({ show: true, rule })} className="text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
        </div>
      ),
    },
  ];

  const modalFooter = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={() => setModalOpen(false)} className="btn-secondary">Cancel</button>
      <button onClick={handleSave} disabled={saving || !formData.ruleName} className="btn-primary disabled:opacity-50">
        {saving ? 'Saving...' : editRule ? 'Update Rule' : 'Add Rule'}
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
          <Shield className="w-5 h-5 text-primary-600" />
          <span>Firewall Rules</span>
        </h3>
        <button onClick={openAdd} className="btn-primary inline-flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Add Rule</span>
        </button>
      </div>

      <div className="card">
        <DataTable columns={columns} data={rules} searchPlaceholder="Search rules..." loading={loading} />
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editRule ? 'Edit ACL Rule' : 'Add ACL Rule'} size="lg" footer={modalFooter}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name <span className="text-red-500">*</span></label>
              <input type="text" value={formData.ruleName} onChange={(e) => setFormData(d => ({ ...d, ruleName: e.target.value }))} placeholder="e.g., Allow-HTTPS" className="input-base w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <input type="number" value={formData.priority} onChange={(e) => setFormData(d => ({ ...d, priority: parseInt(e.target.value) || 100 }))} className="input-base w-full" min={1} max={9999} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Direction</label>
              <select value={formData.direction} onChange={(e) => setFormData(d => ({ ...d, direction: e.target.value as 'inbound' | 'outbound' }))} className="input-base w-full">
                <option value="inbound">Inbound</option>
                <option value="outbound">Outbound</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <select value={formData.action} onChange={(e) => setFormData(d => ({ ...d, action: e.target.value as 'allow' | 'deny' }))} className="input-base w-full">
                <option value="allow">Allow</option>
                <option value="deny">Deny</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Source CIDR</label>
              <input type="text" value={formData.sourceCidr || ''} onChange={(e) => setFormData(d => ({ ...d, sourceCidr: e.target.value }))} placeholder="e.g., 10.0.0.0/8 (any if empty)" className="input-base w-full" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Destination CIDR</label>
              <input type="text" value={formData.destCidr || ''} onChange={(e) => setFormData(d => ({ ...d, destCidr: e.target.value }))} placeholder="e.g., 0.0.0.0/0 (any if empty)" className="input-base w-full" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Protocol</label>
              <select value={formData.protocol || 'all'} onChange={(e) => setFormData(d => ({ ...d, protocol: e.target.value }))} className="input-base w-full">
                <option value="all">All</option>
                <option value="tcp">TCP</option>
                <option value="udp">UDP</option>
                <option value="icmp">ICMP</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Port Range</label>
              <input type="text" value={formData.portRange || ''} onChange={(e) => setFormData(d => ({ ...d, portRange: e.target.value }))} placeholder="e.g., 443 or 8000-9000" className="input-base w-full" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input type="text" value={formData.description || ''} onChange={(e) => setFormData(d => ({ ...d, description: e.target.value }))} placeholder="Optional description" className="input-base w-full" />
          </div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input type="checkbox" checked={formData.enabled} onChange={(e) => setFormData(d => ({ ...d, enabled: e.target.checked }))} className="w-4 h-4 text-primary-600 border-gray-300 rounded" />
            <span className="text-sm text-gray-700">Enabled</span>
          </label>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={deleteConfirm.show}
        title="Delete ACL Rule"
        message={`Delete rule "${deleteConfirm.rule?.rule_name}"?`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteConfirm({ show: false })}
        isLoading={deleting}
      />
    </div>
  );
};

export default ACLTab;
